import { useState, useCallback, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { Upload } from 'lucide-react';
import { api } from '@/lib/api.js';
import { fileDealDocuments, setPolicyNumber as savePolicyNumber, announceDocumentsChanged } from '@/services/deals.service.js';
import useDocumentDrop, { itemOf } from '@/hooks/useDocumentDrop.js';
import DocumentDropRows from '@/components/account/DocumentDropRows.jsx';
import { filedWords } from '@/lib/dropKinds.js';

// THE POLICY DOCUMENTS SHEET (paperwork-1 screen 2, D7): the carrier's policy documents dropped here are filed on the deal's
// policy through the deal's document door (each file preselected from its name or the caller's kind, with the kinds' lines, the name it will be filed
// as, our-own-paper withheld from carrier kinds, and the replace question), the policy number goes through the policy writer
// (setPolicyNumber, PATCH /deal/:id/policy-info), the Traffic Pattern task is cleared, and the notes ride with the filing as
// a broker-internal note on its activity line. A quote is not filed here (the quote drop is on the deal page).
// initialKind (paperwork-1 L2-2): the kind each file is preselected as when a caller states one ("Drop the signed application
// for <policy>" opens it at 'application', "Drop the binder" at 'binder'); stated by no caller, each file is preselected from
// its filename (suggestDropKind, D7; review fix M5). The broker can still change each file's kind.
export default function PolicyDocsDropSheet({ open, onOpenChange, dealId, taskId, onSuccess, initialKind = null }) {
  const [dragOver, setDragOver] = useState(false);
  const [policyNumber, setPolicyNumber] = useState('');
  const drop = useDocumentDrop(dealId);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const files = drop.entries;
  const pending = drop.pending; // settled files (filed, or already filed with every fact stated) are never posted
  const { clear } = drop;
  // Completed: every file filed, the number saved and the task cleared. The button is then Done (it closes), never "Save
  // and complete" again; a new file or an edited number starts another round (L1b-B4, Chrome pass).
  const [completed, setCompleted] = useState(false);
  useEffect(() => { if (!open) { clear(); setCompleted(false); setPolicyNumber(''); } }, [open, clear]);
  useEffect(() => { if (pending.length) setCompleted(false); }, [pending.length]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    // Prompt 173 §2a: this sheet is a declared zone. Without stopPropagation
    // the drop ALSO bubbled (through the portal's React tree) to the
    // workspace handler and was sent to the quote parser.
    e.stopPropagation();
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) drop.add(dropped, { kind: initialKind });
  }, [drop, initialKind]);

  const handleFileSelect = useCallback((e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length) drop.add(selected, { kind: initialKind });
    e.target.value = '';
  }, [drop, initialKind]);

  async function handleSubmit() {
    if (!pending.length && !policyNumber.trim()) {
      // The application drop hides the policy number, so it never asks for one (Chrome pass, round 2).
      toast.error(initialKind === 'application' ? 'Drop the signed application first' : 'Drop at least one file or enter a policy number');
      return;
    }
    if (pending.length && !drop.ready) {
      toast.error(`Answer each file first: ${drop.firstUnanswered() || 'what it is, and whether it replaces one already filed'}`);
      return;
    }

    setSubmitting(true);
    try {
      if (pending.length) {
        const res = await fileDealDocuments(dealId, pending.map(itemOf), { notes });
        drop.applyResults(pending, res.results);
        if (res.results.some((r) => r.ok)) announceDocumentsChanged(dealId);
        const failed = res.results.filter((r) => !r.ok);
        for (const r of res.results.filter((x) => x.ok)) toast.success(`${r.filename} filed as ${r.document?.name || r.name}`);
        if (failed.length) {
          for (const r of failed) toast.error(`${r.filename}: ${r.error}`);
          return; // the rows say why; the number and the task wait until every file is filed
        }
      }

      // Every outcome says its own result (main 46c07e1, David 2026-10-30: N6788P and N820T "fail silently"): the number and
      // the task each name what happened; a filed document is never reported as a failed number, or the reverse.
      const why = (err) => err?.body?.error || err?.message || 'unknown error';
      if (policyNumber.trim()) {
        // The policy writer (policy-info door), never an inline CRM PATCH.
        try { await savePolicyNumber(dealId, policyNumber.trim()); }
        catch (err) {
          toast.error(`Policy number ${policyNumber.trim()} NOT saved: ${why(err)}`);
          if (pending.length) onSuccess?.(); // re-read what did land
          return;
        }
      }

      if (taskId) {
        // `api` is an object of verbs: the bare call here threw "api is not a function" before the task was ever cleared.
        try { await api.post(`/api/tracon/command-center/tasks/${encodeURIComponent(taskId)}/clear`, { reason: 'satisfied' }); }
        catch (err) { toast.error(`Filed, but the task did not clear: ${why(err)}`); }
      }

      // Chrome passes, L2-2 and round 2: the words say what was filed (lib/dropKinds.js filedWords).
      toast.success(filedWords(pending.map((e) => itemOf(e).kind)));
      setCompleted(true);
      onSuccess?.();
    } catch (err) {
      toast.error(`Filing failed: ${err?.body?.error || err.message || 'unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          {/* L2-2 Chrome pass: opened for the signed application, the sheet says so (it is the client's paper, not the carrier's). */}
          <SheetTitle>{initialKind === 'application' ? 'Drop the signed application' : 'Upload policy documents'}</SheetTitle>
          <SheetDescription>
            {initialKind === 'application'
              ? 'Files the application the client signed on this policy (each file starts as Signed application; change it if it is something else)'
              : <>Files the carrier&apos;s policy documents on this policy and saves its policy number</>}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div hidden={initialKind === 'application'}>
            <label className="text-xs font-medium text-text-muted">Policy Number</label>
            <input
              type="text"
              value={policyNumber}
              onChange={(e) => { setPolicyNumber(e.target.value); setCompleted(false); }}
              placeholder="e.g. AVN-2026-001234"
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted">Documents</label>
            <div
              onDrop={(e) => { setDragOver(false); handleDrop(e); }}
              onDragOver={(e) => e.preventDefault()}
              onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
              className={`mt-1 flex min-h-[120px] cursor-pointer items-center justify-center rounded border-2 border-dashed p-4 transition-colors ${dragOver ? 'border-accent bg-accent-soft/30' : 'border-border bg-bg/50 hover:border-accent/40'}`}
              onClick={() => document.getElementById('policy-file-input')?.click()}
            >
              <div className="text-center">
                <Upload size={24} className="mx-auto text-text-dim" strokeWidth={1.5} />
                <p className="mt-2 text-xs text-text-muted">
                  Drop files here or <span className="text-accent">browse</span>
                </p>
              </div>
              <input
                id="policy-file-input"
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          </div>

          {files.length > 0 && <DocumentDropRows drop={drop} exclude={['quote']} disabled={submitting} />}

          <div>
            <label className="text-xs font-medium text-text-muted">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Broker-only notes for this filing…"
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </div>

          {/* Done also when every file here is already settled (filed, or already uploaded: round 2 Q8) and no number is typed:
              nothing is left to do, so the button never offers a save that can only refuse (Chrome pass, round 2). */}
          {completed || (files.length > 0 && !pending.length && !policyNumber.trim()) ? (
            <Button onClick={() => onOpenChange?.(false)} className="w-full">Done</Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="w-full">
              {submitting ? 'Filing…' : pending.length ? `File ${pending.length === 1 ? 'it' : `${pending.length} files`} and complete` : 'Save and complete'}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
